import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AuthenticationError, AuthorizationError, NetworkError, PARSE_APP_DATABASE } from '@back4app2/sdk';
import back4app2 from '../../back4app2';
import { BACK4APP_DOT_COM_SITE_URL } from '../../settings';

/**
 * Progress stages during app creation:
 * - creating-app: Parse app is being created
 * - connecting: Agent is being connected to the app
 * - done: All steps complete
 */
type CreateProgress = 'creating-app' | 'connecting' | 'done';

export type CreateAppPhase = 'idle' | 'running' | 'success' | 'error';

interface CreateAppModalProps {
  open: boolean;
  agentId?: string;
  defaultAppName: string;
  onClose: () => void;
  onDone: () => void;
  // Controlled phase (to survive AppPanel remounts)
  phase?: CreateAppPhase;
  onPhaseChange?: (phase: CreateAppPhase) => void;
}

/**
 * CreateAppModal
 *
 * Value-first UX: show what the user is getting while the backend work happens.
 * Implementation is optimistic (no real backend progress events).
 */
export default function CreateAppModal({
  open,
  agentId,
  defaultAppName,
  onClose,
  onDone,
  phase: externalPhase,
  onPhaseChange,
}: CreateAppModalProps) {
  // Use controlled phase if provided, otherwise use internal state
  const isPhaseControlled = typeof externalPhase === 'string' && typeof onPhaseChange === 'function';
  const [internalPhase, setInternalPhase] = useState<CreateAppPhase>('idle');
  
  const phase = isPhaseControlled ? externalPhase : internalPhase;
  const setPhase = useCallback((p: CreateAppPhase | ((prev: CreateAppPhase) => CreateAppPhase)) => {
    if (isPhaseControlled) {
      const newPhase = typeof p === 'function' ? p(externalPhase!) : p;
      onPhaseChange?.(newPhase);
    } else {
      setInternalPhase(p);
    }
  }, [isPhaseControlled, externalPhase, onPhaseChange]);

  const [appName, setAppName] = useState(defaultAppName);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const [createProgress, setCreateProgress] = useState<CreateProgress>('creating-app');
  const [isBackendReady, setIsBackendReady] = useState(false);

  // Track if we've already initialized for this modal session
  const hasInitializedRef = useRef(false);

  // Derived: creating phase is fully done (ready to proceed)
  const isCreateComplete = phase === 'running' && createProgress === 'done' && isBackendReady;

  // Reset hasInitializedRef when modal closes
  useEffect(() => {
    if (!open) {
      hasInitializedRef.current = false;
    }
  }, [open]);

  // When component mounts with phase='running', assume backend is ready
  // This happens when AppPanel remounts after the app is created
  useEffect(() => {
    if (open && phase === 'running' && !hasInitializedRef.current) {
      // Component was remounted during 'running' phase
      // Assume the operation completed successfully
      setCreateProgress('done');
      setIsBackendReady(true);
      hasInitializedRef.current = true;
    }
  }, [open, phase]);

  // Reset local state when opening (only once per session)
  useEffect(() => {
    if (!open) return;
    
    // Don't reset if we're already in an active phase (running/success)
    // This happens when AppPanel remounts during the create operation
    if (phase === 'running' || phase === 'success') {
      // Mark as initialized to prevent future resets
      hasInitializedRef.current = true;
      return;
    }
    
    // Don't reset if we've already initialized in this session
    if (hasInitializedRef.current) {
      return;
    }
    
    hasInitializedRef.current = true;
    setAppName(defaultAppName);
    setPhase('idle');
    setErrorMessage(undefined);
    setCreateProgress('creating-app');
    setIsBackendReady(false);
  }, [open, phase, setPhase, defaultAppName]);

  const closeIfIdle = useCallback(() => {
    // Only allow closing in safe phases. During running/success, user must click Get started
    // to proceed (this also ensures preview gating is released intentionally).
    if (phase === 'running' || phase === 'success') return;
    onClose();
  }, [onClose, phase]);

  const runCreate = useCallback(async () => {
    if (!agentId) {
      setErrorMessage('Missing agent identifier. Please go back and try again.');
      setPhase('error');
      return;
    }

    const finalName = (appName || '').trim();
    if (!finalName) {
      setErrorMessage('Please enter an app name.');
      setPhase('error');
      return;
    }

    try {
      setPhase('running');
      setErrorMessage(undefined);
      setCreateProgress('creating-app');
      setIsBackendReady(false);

      // Get parse versions to determine which version to use
      const versions = await back4app2.listParseVersion();
      const parseVersion =
        versions?.parseVersionList?.[0]?.version ||
        versions?.parseVersionList?.[0]?.id ||
        'latest';

      // NOTE: If captcha is required, the API expects a token; we will surface a friendly error for now.
      const needsCaptcha = Boolean(versions?.showCaptcha);

      if (needsCaptcha) {
        throw new Error(
          'Captcha verification is required to create an app. Please complete signup verification and try again.'
        );
      }

      // Create the Parse app with MongoDB (NOSQL)
      const created = await back4app2.createParseApp(
        finalName,
        false, // isPublic
        String(parseVersion),
        null, // reCaptcha
        PARSE_APP_DATABASE.NOSQL
      );

      const dashboardId = created?.id;
      if (!dashboardId) {
        throw new Error('App creation did not return an id. Please try again.');
      }

      // Move to connecting stage
      setCreateProgress('connecting');

      // Link app to agent. The backend will run a deterministic sync and only
      // return when the Agent has the required app metadata (can take longer in some cases).
      await back4app2.setAgentCurrentParseApp(agentId, dashboardId, true);

      // All done
      setCreateProgress('done');
      setIsBackendReady(true);
    } catch (e) {
      if (e instanceof NetworkError) {
        setErrorMessage('Network error while creating your app. Please check your connection and try again.');
      } else if (e instanceof AuthenticationError || e instanceof AuthorizationError) {
        window.location.replace(
          `${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`
        );
        return;
      } else {
        console.error('Error creating app', e);
        setErrorMessage((e as any)?.message || 'Unexpected error while creating app. Please try again.');
      }
      setPhase('error');
    }
  }, [agentId, appName, setPhase]);

  const handleGetStarted = useCallback(() => {
    onDone();
  }, [onDone]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-xl bg-[#1e293b]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.5)] overflow-hidden">
        <div className="relative p-6">
          {/* Close button - hide during running and success */}
          {phase !== 'running' && phase !== 'success' && (
            <button
              className="absolute right-4 top-4 text-white/40 hover:text-white/80 transition-colors"
              onClick={closeIfIdle}
              type="button"
              title="Close"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18" />
                <path d="M6 6l12 12" />
              </svg>
            </button>
          )}

          <div className="flex flex-col items-center">
            {/* Center icon - spinner or check with glow */}
            <div
              className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-500 ${
                phase === 'running' && isCreateComplete
                  ? 'bg-[#27AE60]/20 shadow-[0_0_40px_rgba(39,174,96,0.4)]'
                  : phase === 'success'
                  ? 'bg-[#27AE60]/20 shadow-[0_0_40px_rgba(39,174,96,0.4)]'
                  : phase === 'running'
                  ? 'bg-[#208AEC]/15 shadow-[0_0_25px_rgba(32,138,236,0.3)]'
                  : 'bg-[#208AEC]/10'
              }`}
            >
              {(phase === 'running' && isCreateComplete) || phase === 'success' ? (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-[#27AE60] animate-[scaleIn_0.3s_ease-out]">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              ) : phase === 'running' ? (
                <div className="w-8 h-8 rounded-full border-[3px] border-[#208AEC]/30 border-t-[#208AEC] animate-spin" />
              ) : (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-[#208AEC]">
                  <ellipse cx="12" cy="5" rx="9" ry="3" />
                  <path d="M21 5v14c0 1.66-4.03 3-9 3s-9-1.34-9-3V5" />
                  <path d="M21 12c0 1.66-4.03 3-9 3s-9-1.34-9-3" />
                </svg>
              )}
            </div>

            <h2 className="mt-4 text-xl font-semibold text-white text-center font-sora">
              {phase === 'running'
                ? isCreateComplete
                  ? 'Your web app is ready 🎉'
                  : createProgress === 'connecting'
                  ? 'Almost ready…'
                  : 'Setting up your app…'
                : phase === 'success'
                ? 'Your web app is ready 🎉'
                : 'Create your App'}
            </h2>

            <p className="mt-2 text-sm text-white/60 text-center max-w-lg">
              {phase === 'running'
                ? isCreateComplete
                  ? 'Your Agent is now running on a cloud App — ready to store data and scale automatically.'
                  : createProgress === 'connecting'
                  ? 'Just finishing the setup.'
                  : "We're preparing everything your app needs to run in the cloud."
                : phase === 'success'
                ? 'Your Agent is now running on a cloud App — ready to store data and scale automatically.'
                : "Choose a name for your App. We'll create a cloud app with data storage, APIs, authentication, and hosting."}
            </p>
          </div>

          {phase === 'idle' ? (
            <div className="mt-6">
              <label className="text-sm text-light-blue font-medium">App name</label>
              <input
                value={appName}
                onChange={(e) => setAppName(e.target.value)}
                className="mt-2 w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-center placeholder-white/30 focus:outline-none focus:border-[#208AEC]/50 transition-colors"
                placeholder="My App"
              />

              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 transition-colors"
                  type="button"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 rounded-xl bg-[#27AE60] hover:bg-[#19875F] text-white font-medium transition-colors"
                  onClick={runCreate}
                >
                  Create
                </button>
              </div>
            </div>
          ) : null}

          {phase === 'running' ? (
            <div className="mt-6">
              {/* Progress Checklist — the protagonist */}
              <div className="mx-auto max-w-sm">
                <div className="bg-[#0d1420]/60 border border-white/10 rounded-xl p-4 space-y-3">
                  {/* Step 1: Creating App */}
                  <div className="flex items-center gap-3">
                    {createProgress === 'creating-app' ? (
                      // In progress - spinner
                      <div className="w-6 h-6 rounded-full bg-[#208AEC]/20 flex items-center justify-center flex-shrink-0">
                        <div className="w-3.5 h-3.5 rounded-full border-2 border-[#208AEC]/30 border-t-[#208AEC] animate-spin" />
                      </div>
                    ) : (
                      // Done - checkmark
                      <div className="w-6 h-6 rounded-full bg-[#27AE60]/20 flex items-center justify-center flex-shrink-0 animate-[scaleIn_0.3s_ease-out]">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="text-[#27AE60]">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      </div>
                    )}
                    <span className="text-sm text-white font-medium">
                      {createProgress === 'creating-app' ? 'Creating App' : 'App created'}
                    </span>
                  </div>

                  {/* Step 2: Connecting Agent and App */}
                  <div className="flex items-center gap-3">
                    {createProgress === 'creating-app' ? (
                      // Pending - empty circle
                      <div className="w-6 h-6 rounded-full bg-white/5 border border-white/20 flex items-center justify-center flex-shrink-0">
                        <div className="w-2 h-2 rounded-full bg-white/20" />
                      </div>
                    ) : createProgress === 'connecting' ? (
                      // In progress - spinner
                      <div className="w-6 h-6 rounded-full bg-[#208AEC]/20 flex items-center justify-center flex-shrink-0">
                        <div className="w-3.5 h-3.5 rounded-full border-2 border-[#208AEC]/30 border-t-[#208AEC] animate-spin" />
                      </div>
                    ) : (
                      // Done - checkmark
                      <div className="w-6 h-6 rounded-full bg-[#27AE60]/20 flex items-center justify-center flex-shrink-0 animate-[scaleIn_0.3s_ease-out]">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="text-[#27AE60]">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      </div>
                    )}
                    <span className={`text-sm font-medium ${
                      createProgress === 'creating-app' ? 'text-white/40' : 'text-white'
                    }`}>
                      {createProgress === 'done' ? 'Agent and App connected' : 'Connecting Agent and App'}
                    </span>
                  </div>
                </div>
              </div>

              {/* CTA Button */}
              <div className="mt-8 flex items-center justify-center">
                <button
                  type="button"
                  onClick={handleGetStarted}
                  disabled={!isBackendReady}
                  aria-busy={!isBackendReady}
                  className={`
                    px-7 py-3 rounded-xl text-white font-semibold tracking-wide
                    transition-all duration-200
                    min-w-[170px]
                    ${
                      isBackendReady
                        ? 'shadow-[0_0_20px_rgba(39,174,96,0.4)] hover:shadow-[0_0_30px_rgba(39,174,96,0.6)] transform hover:scale-[1.02]'
                        : 'opacity-60 cursor-not-allowed shadow-none'
                    }
                  `}
                  style={{ backgroundColor: isBackendReady ? '#27AE60' : '#374151' }}
                  onMouseEnter={(e) => {
                    if (isBackendReady) e.currentTarget.style.backgroundColor = '#19875F';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = isBackendReady ? '#27AE60' : '#374151';
                  }}
                >
                  {isBackendReady ? (
                    'Get started'
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Building…
                    </span>
                  )}
                </button>
              </div>
            </div>
          ) : null}

          {phase === 'error' ? (
            <div className="mt-6">
              <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl p-3">
                {errorMessage || 'Unexpected error while creating app. Please try again.'}
              </div>
              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 transition-colors"
                  type="button"
                  onClick={onClose}
                >
                  Close
                </button>
                <button
                  className="px-4 py-2 rounded-xl bg-[#27AE60] hover:bg-[#19875F] text-white font-medium transition-colors"
                  type="button"
                  onClick={runCreate}
                >
                  Retry
                </button>
              </div>
            </div>
          ) : null}

          {phase === 'success' ? (
            <div className="mt-8 flex items-center justify-center">
              <button
                type="button"
                onClick={handleGetStarted}
                className="px-7 py-3 rounded-xl text-white font-semibold tracking-wide shadow-[0_0_20px_rgba(39,174,96,0.4)] hover:shadow-[0_0_30px_rgba(39,174,96,0.6)] transform hover:scale-[1.02] transition-all duration-200"
                style={{ backgroundColor: '#27AE60' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#19875F'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#27AE60'; }}
              >
                Get started
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
